import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { WorkerFile } from '../../api/workerFilesApi';
import { 
    SessionQuestionEntityFrontend,
    bulkUpdateSessionQuestionAnswers 
} from '../../api/sessionQuestionsApi';

interface FileParseResult {
    jobId: string;
    workerId: string;
    success: boolean;
    parsedAnswers: Record<string, any>;
    answeredQuestions: number;
    totalQuestions: number;
    fileMetadata?: {
        fileName: string;
        mimeType: string;
        processedAt: string;
        mediaId?: string;
    };
    timestamp: string;
}

interface FileParseResultsModalProps {
    open: boolean;
    onClose: () => void;
    file: WorkerFile | null;
    workerId: string;
    questions: SessionQuestionEntityFrontend[];
    onSaveComplete: (updatedQuestions: SessionQuestionEntityFrontend[]) => void;
    showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

export const FileParseResultsModal = React.forwardRef<FileParseResultsModalRef, FileParseResultsModalProps>(({
    open,
    onClose,
    file,
    workerId,
    questions,
    onSaveComplete,
    showSnackbar,
}, ref) => {
    const [parseResult, setParseResult] = useState<FileParseResult | null>(null);
    const [waiting, setWaiting] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open && file) {
            // Reset state when opening
            setParseResult(null);
            setWaiting(true);
        }
    }, [open, file]);

    // This will be called by the parent component when parse results are received
    const handleParseResults = (results: FileParseResult) => {
        console.log('FileParseResultsModal: handleParseResults called with:', results);
        setParseResult(results);
        setWaiting(false);
    };

    const handleSaveParsedResults = async () => {
        if (!parseResult || !parseResult.parsedAnswers) {
            showSnackbar('No parsed results to save', 'warning');
            return;
        }

        setSaving(true);
        try {
            // Convert parsed answers to bulk update format
            const updates: Array<{ sessionQuestionId: number; answer: string }> = [];
            
            for (const [key, value] of Object.entries(parseResult.parsedAnswers || {})) {
                const question = questions.find(q => 
                    (q.questionDataKey && q.questionDataKey === key) || 
                    (q.questionKey && q.questionKey === key)
                );
                
                if (question && value !== null && value !== undefined) {
                    // Convert value to string format expected by the API
                    let answerValue = value;
                    if (typeof value === 'object') {
                        answerValue = JSON.stringify(value);
                    } else if (typeof value !== 'string') {
                        answerValue = String(value);
                    }
                    
                    updates.push({
                        sessionQuestionId: question.id,
                        answer: answerValue
                    });
                }
            }
            
            if (updates.length === 0) {
                showSnackbar('No matching questions found to update', 'info');
                setSaving(false);
                return;
            }
            
            // Use bulk update API
            const updatedQuestions = await bulkUpdateSessionQuestionAnswers(workerId, updates);
            
            showSnackbar(`Successfully saved ${updates.length} answers from file parsing!`, 'success');
            onSaveComplete(updatedQuestions);
            onClose();
        } catch (error: any) {
            showSnackbar(`Failed to save parsed results: ${error.message}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    const getMappedQuestions = () => {
        if (!parseResult?.parsedAnswers) return { mapped: [], unmapped: [] };

        const mappedQuestions: Array<{ key: string; question: string; value: any }> = [];
        const unmappedKeys: Array<{ key: string; value: any }> = [];
        
        for (const [key, value] of Object.entries(parseResult.parsedAnswers || {})) {
            const question = questions.find(q => 
                (q.questionDataKey && q.questionDataKey === key) || 
                (q.questionKey && q.questionKey === key)
            );
            
            if (question && value !== null && value !== undefined) {
                mappedQuestions.push({
                    key,
                    question: question.questionShorten || question.question,
                    value
                });
            } else if (value !== null && value !== undefined) {
                unmappedKeys.push({ key, value });
            }
        }

        return { mapped: mappedQuestions, unmapped: unmappedKeys };
    };

    // Expose this method so parent can call it
    React.useImperativeHandle(ref, () => ({
        handleParseResults
    }));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        Parse Results - {file?.originalFileName || 'File'}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {waiting ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Processing file... Waiting for AI response...
                        </Typography>
                    </Box>
                ) : parseResult ? (
                    <Box>
                        <Alert severity={parseResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                            {parseResult.success 
                                ? `Successfully extracted ${parseResult.answeredQuestions} out of ${parseResult.totalQuestions} questions`
                                : 'File parsing failed'
                            }
                        </Alert>
                        
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Parsed Results:</Typography>
                        <Paper sx={{ p: 2, maxHeight: 300, overflow: 'auto', mb: 2 }}>
                            <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                                {JSON.stringify(parseResult.parsedAnswers, null, 2)}
                            </pre>
                        </Paper>
                        
                        {/* Show mapping preview */}
                        {parseResult.success && (() => {
                            const { mapped, unmapped } = getMappedQuestions();
                            
                            if (mapped.length === 0 && unmapped.length === 0) {
                                return <Typography variant="body2" color="text.secondary">No matching questions found</Typography>;
                            }
                            
                            return (
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Questions to be updated:</Typography>
                                    <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                                        {mapped.map(({ key, question, value }) => (
                                            <Box key={key} sx={{ display: 'flex', mb: 0.5 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium', mr: 1 }}>
                                                    {question}:
                                                </Typography>
                                                <Typography variant="body2">
                                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                </Typography>
                                            </Box>
                                        ))}
                                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                                            {mapped.length} questions will be updated
                                        </Typography>
                                        
                                        {unmapped.length > 0 && (
                                            <>
                                                <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block', fontWeight: 'bold' }}>
                                                    Unmapped data (no matching questions found):
                                                </Typography>
                                                {unmapped.map(({ key, value }) => (
                                                    <Box key={key} sx={{ display: 'flex', mt: 0.5 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                                            {key}:
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {String(value)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                                                    Check if questionKey values match in the database
                                                </Typography>
                                            </>
                                        )}
                                    </Paper>
                                </Box>
                            );
                        })()}
                        
                        {parseResult.fileMetadata && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    File: {parseResult.fileMetadata.fileName} | 
                                    Processed at: {new Date(parseResult.timestamp).toLocaleString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Alert severity="error">No parse results available</Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                {parseResult && parseResult.success && (
                    <Button 
                        onClick={handleSaveParsedResults} 
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Results to Database'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
});

FileParseResultsModal.displayName = 'FileParseResultsModal';

// Export a ref type to allow parent to call handleParseResults
export interface FileParseResultsModalRef {
    handleParseResults: (results: FileParseResult) => void;
}